import type { Rect } from "@/lib/math/rect";
import type { DisplayItem } from "@/renderer/scene/displayList";
import {
  displayItemVisualBounds,
  intersectBounds,
  rectsIntersect,
} from "@/renderer/scene/displayItemGeometry";
import type { RenderTile } from "./TileManager";

/** Bin display items into visible tiles once per compose pass. */
export function binDisplayItemsToTiles(items: DisplayItem[], tiles: RenderTile[]): Map<string, DisplayItem[]> {
  const bins = new Map<string, DisplayItem[]>();
  if (tiles.length === 0) {
    return bins;
  }

  for (const tile of tiles) {
    bins.set(tile.key, []);
  }

  const tileByKey = new Map(tiles.map(entry => [entry.key, entry]));
  const sample = tiles[0]!;
  const worldTileSize = sample.bounds.width;
  const variantKey = sample.variantKey;
  const zoomBucket = sample.zoomBucket;

  for (const item of items) {
    const visualBounds = displayItemVisualBounds(item);
    const bounds = item.clip ? intersectBounds(visualBounds, item.clip.bounds) : visualBounds;
    if (bounds.width <= 0 || bounds.height <= 0) {
      continue;
    }

    const startX = Math.floor(bounds.x / worldTileSize);
    const endX = Math.floor((bounds.x + bounds.width) / worldTileSize);
    const startY = Math.floor(bounds.y / worldTileSize);
    const endY = Math.floor((bounds.y + bounds.height) / worldTileSize);

    for (let tileY = startY; tileY <= endY; tileY += 1) {
      for (let tileX = startX; tileX <= endX; tileX += 1) {
        const key = `${variantKey}:${zoomBucket}:${tileX}:${tileY}`;
        const tile = tileByKey.get(key);
        const bin = bins.get(key);
        if (!tile || !bin || !rectsIntersect(bounds, tile.bounds)) {
          continue;
        }
        bin.push(item);
      }
    }
  }

  return bins;
}
